import FileView from "components/fileView";
import DynamicFormInput from "components/form/form";
import { useToast } from "contexts/ToastContext";
import { useState } from "react";
import { updateLegalTranslation } from "services/legalTranslationService";
import { LoadingIcon } from "utils/constant";
import { SECTOR_DIRECTIVES_REQUEST_STATUS } from "utils/constantValues";
import { formatDay } from "utils/helpers";
import FileUpload from "components/FileUpload";
import DocumentViewerModal from "components/DocumentViewer";
import { useTranslation } from "react-i18next";
import { appendOfficeStamp } from "services/stampService";

interface RegistrarLegalTranslationProps {
    request: any;
}

export default function RegistrarLegalTranslation({ request }: RegistrarLegalTranslationProps) {
    const { t } = useTranslation();
    const [letterRefNum, setLetterRefNum] = useState("");
    const [completionLetter, setCompletionLetter] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLetterRefNum(e.target.value);
    };

    const handleFileChange = (file: any) => {
        setCompletionLetter(file);
    };

    const handleSave = async () => {
        setSubmitting(true);
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.letter_refnum_submitted.value);
        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value);
        data.append("registry_number", letterRefNum);
        if (completionLetter) {
            data.append("research_files", completionLetter);
            // Office stamp from HRMS: the backend stamps the PDF before storing it.
            await appendOfficeStamp(data);
        }

        try {
            const response = await updateLegalTranslation(request.id, data);
            showToast(t("LetterReferenceNumberAndCompletionLetterUploaded") || "Letter Reference Number and Completion Letter uploaded successfully!", "success");
            // Auto-refresh to show the updated data
            window.location.reload();
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadLetterReferenceNumberAndCompletionLetter") || "Failed to upload Letter Reference Number and Completion Letter.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex w-full gap-8">
            {/* Left Panel - Legal Translation Details */}
            <div className="flex-1 bg-gray-100 p-2">
                <div className="flex flex-col max-w-4xl bg-white rounded-lg shadow-md p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between pb-4 mb-3">
                        <div className="flex items-center space-x-2">
                            <h1 className="text-2xl font-semibold text-gray-800">
                                {t("LegalTranslationDetails") || "Legal Translation Details"}
                            </h1>
                        </div>
                    </div>

                    {/* Summary Written Section */}
                    {request?.documents.find((document: any) => document.document_type === "draft_translation_file") && (
                        <div className="flex-1 bg-white mb-4 p-4 rounded-lg border border-gray-400">
                            <div className="pb-2 border-b border-gray-200 mb-4">
                                <p>{t("SummaryWritten") || "Summary Written"}</p>
                            </div>
                            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(request?.created_at).rest}</p>
                            <p className="font-regular text-base text-[#606060]">{request?.translated_summary}</p>
                            {/* Show latest draft translation file */}
                            {(() => {
                                const draftTranslationDocuments = request?.documents.filter((document: any) => document.document_type === "draft_translation_file");
                                if (draftTranslationDocuments.length === 0) return null;

                                const sortedDocuments = [...draftTranslationDocuments].sort((a, b) => {
                                    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                                    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                                    return dateB - dateA;
                                });
                                const latestDocument = sortedDocuments[0];

                                return (
                                    <div className="mt-4">
                                        <DocumentViewerModal
                                            documents={[latestDocument]}
                                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Documents Section */}
                    <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
                        {request?.documents.find((document: any) => document.document_type === "translation_request_document") && (
                            <>
                                {request?.registry_number &&
                                    <p className="text-[#432101AB] font-normal text-base">
                                        {t("RegisterNumber") || "Register Number"}: {request.registry_number}
                                    </p>
                                }
                                <p className="text-[#432101AB] font-normal text-base">
                                    {t("RegisterLetterNumber") || "Register Letter Number"}: {request?.request_letter_reference_number}
                                </p>
                                <DocumentViewerModal
                                    documents={request?.documents.filter((document: any) => document.document_type === "translation_request_document")}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                                />
                            </>
                        )}
                        {request?.documents.find((document: any) => document.document_type === "traslation_request_letter_file") && (
                            <DocumentViewerModal
                                documents={request?.documents.filter((document: any) => document.document_type === "traslation_request_letter_file")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_translation/legal-translation-documents/document`}
                            />
                        )}
                        <div className="mb-8">
                            <p className="text-[#432101AB] font-normal text-base">
                                {t("LegalTranslationSummaryNote") || "Legal Translation Summary Note"}
                            </p>
                            <p className="text-gray-500 font-medium mb-2">
                                {request?.request_summary}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Panel - Registrar Input Form */}
            <div className="flex flex-col flex-1 max-w-4xl bg-white rounded-lg shadow-md p-6 mb-4 h-fit overflow-hidden">
                <div className="-mx-6 -mt-6 mb-5 bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-6 py-3">
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
                    <p className="text-white font-semibold text-base">{t("LetterReferenceNumber") || "Letter Reference Number"}</p>
                </div>
                <DynamicFormInput
                    inputs={[
                        {
                            value: letterRefNum,
                            type: "text",
                            label: t("LetterReferenceNumber") || "Letter Reference Number",
                            name: "letter_reference_number",
                            onChange: handleChange,
                            placeholder: t("LetterReferenceNumberPlaceholder") || "Eg. 12345",
                        },
                    ]}
                />
                <div className="mb-6">
                    <label
                        htmlFor="completion-letter"
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        {t("TranslationCompletionLetter") || "Translation Completion Letter"}
                    </label>
                    <div className="relative">
                        <FileUpload onChange={handleFileChange} />
                    </div>
                </div>
                <button 
                    onClick={handleSave} 
                    disabled={submitting}
                    className="bg-[#0A1D39] hover:bg-[#13294d] mt-4 text-white font-bold py-3 px-8 rounded-md w-full disabled:opacity-70"
                >
                    {submitting ? (
                        <div className="flex items-center justify-center">
                            {LoadingIcon} {t("Submitting") || "Submitting..."}
                        </div>
                    ) : (
                        t("Save") || "Save"
                    )}
                </button>
            </div>
        </div>
    );
}